/* eslint-disable react/no-array-index-key */
import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MUITable from '@mui/material/Table';
import Paper from '@mui/material/Paper';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import styled from '@mui/styles/styled';
import PropTypes from 'prop-types';
import { SortsPropType, columnConfigPropType } from './propTypes';
import { TABLE_SETTING } from '../../constants/Tables';
import NoResults from '../NoResults';

const sortingDirections = ['desc', 'asc'];

const NoResultsCell = styled('td')(({ theme }) => ({
	position: 'absolute',
	left: '50%',
	marginLeft: -20,
	marginTop: theme.spacing(2),
}));

const LoadingCell = styled(NoResultsCell)({
	top: '50%',
	marginTop: -20,
});

function MUITableToolbar({ children }) {
	return (
		<Toolbar
			sx={{
				marginBottom: '-.5rem',
				marginRight: { xs: '1.25rem', lg: '.75rem' },
			}}
			inputpros={{
				'aria-label': 'Table',
				role: 'toolbar',
				'aria-controls': 'textarea',
			}}
		>
			{children}
		</Toolbar>
	);
}
MUITableToolbar.propTypes = {
	children: PropTypes.node,
};

function MUITableHead({
	columnConfig,
	columnOrder: _columnOrder,
	columnVisibility: _columnVisbility,
	renderColumnHeader = (key) => key,
	sorts = [],
	onSort = (any) => any,
}) {
	const columnOrder = _columnOrder || Object.keys(columnConfig);
	const columnVisibility =
		_columnVisbility ||
		columnOrder
			.map((key) => ({ [key]: true }))
			.reduce((a, b) => ({ ...a, ...b }));

	const columns = columnOrder
		.filter((key) => columnVisibility[key])
		.map((key) => ({ key, ...columnConfig[key] }));

	return (
		<TableHead>
			<TableRow component="tr" scope="row">
				{columns.map(({ key, isSortable }) => {
					const existingSortIndex = sorts.findIndex((sort) => sort.key === key);
					const existingSort = sorts[existingSortIndex];

					return (
						<TableCell
							key={key}
							scope="col"
							sx={{
								minWidth: '9rem',
								lineHeight: '1.25rem',
								width: 'auto',
								maxWidth: '12rem',
							}}
						>
							{isSortable ? (
								<TableSortLabel
									active={!!existingSort}
									direction={existingSort?.direction || 'desc'}
									onClick={() => {
										const existingDirection = sortingDirections.indexOf(
											existingSort?.direction
										);

										const newDirectionIndex =
											(existingDirection + 1) % sortingDirections.length;

										const direction = sortingDirections[newDirectionIndex]; // grab next item in array, loop to beginning if at end
										const newSort = {
											key: direction ? key : undefined,
											direction,
										};

										const newSorts = []; // clear sorts but we can sort by multiples columns by changing it to [...sorts]

										if (existingSort) {
											newSorts[existingSortIndex] = newSort;
										} else {
											newSorts.push(newSort);
										}

										onSort(newSorts.filter((sort) => !!sort.key));
									}}
								>
									{renderColumnHeader(key)}
								</TableSortLabel>
							) : (
								renderColumnHeader(key)
							)}
						</TableCell>
					);
				})}
			</TableRow>
		</TableHead>
	);
}

MUITableHead.propTypes = {
	sorts: SortsPropType,
	onSort: PropTypes.func,
	renderColumnHeader: PropTypes.func,
	columnOrder: PropTypes.array,
	columnVisibility: PropTypes.object,
	columnConfig: columnConfigPropType,
};

function Table({
	items = [],
	total = 0,
	onPageChange,
	pagination: { limit, page } = {},
	columnConfig,
	columnOrder: _columnOrder,
	columnVisibility: _columnVisbility,
	isLoading,
	renderColumnHeader = (key) => key,
	noResultsContent = <NoResults />,
	TableRowProps = {},
	TableRowComponent = TableRow,
	TableBodyComponent = TableBody,
	TableHeadComponent = TableHead,
	TableCellComponent = TableCell,
	defaultCellValue = '-',
	children,
	onSort = () => {},
	sorts = [],
}) {
	const { t } = useTranslation();
	const columnOrder = _columnOrder || Object.keys(columnConfig);
	const columnVisibility =
		_columnVisbility ||
		columnOrder
			.map((key) => ({ [key]: true }))
			.reduce((a, b) => ({ ...a, ...b }));
	const columns = columnOrder
		.filter((key) => columnVisibility[key])
		.map((key) => ({ key, ...columnConfig[key] }));
	const hasResults = !!items.length;

	return (
		<Box sx={{ width: '100%' }}>
			<Paper
				sx={{
					height: '100%',
					width: '100%',
					maxWidth: { xs: '100%', sm: '100%', md: `calc(100vw - 21.80rem)` },
					overflow: 'hidden',
					margin: '0 auto',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
					{!!children && <MUITableToolbar>{children}</MUITableToolbar>}
				</div>
				<TableContainer
					sx={{
						minHeight: {
							xs: '100vh',
							sm: '80vh',
							md: '74vh',
						},
						maxHeight: {
							xs: '100vh',
							sm: '80vh',
							md: '74vh',
						},
					}}
				>
					<MUITable
						stickyHeader
						sx={{ minWidth: 345, width: '100%' }}
						role="table"
						aria-labelledby="orders table"
					>
						<MUITableHead
							columnConfig={columnConfig}
							columnOrder={columnOrder}
							columnVisibility={columnVisibility}
							renderColumnHeader={renderColumnHeader}
							onSort={onSort}
							sorts={sorts}
						/>
						{isLoading || !hasResults ? (
							<TableBody>
								<TableRow>
									{isLoading && (
										<LoadingCell>
											<CircularProgress />
										</LoadingCell>
									)}
								</TableRow>
							</TableBody>
						) : (
							<TableBody>
								{items.length > 0 &&
									items.map((item, rowIndex) => {
										const rowProps =
											typeof TableRowProps === 'function'
												? TableRowProps(item, rowIndex)
												: TableRowProps;

										const { key, ...restRowProps } = rowProps || {};

										return (
											<TableRow
												key={key}
												aria-label="orders"
												tabIndex={-1}
												role="row"
												{...restRowProps}
											>
												{columns.map(({ getter, formatter }, columnIndex) => {
													const value = formatter(getter(item, columnIndex));
													return (
														<TableCell
															key={`${rowIndex}-${columnIndex}`}
															role="cell"
														>
															{!!value || value === 0
																? value
																: defaultCellValue}
														</TableCell>
													);
												})}
											</TableRow>
										);
									})}
							</TableBody>
						)}
					</MUITable>
					{!hasResults && !isLoading && noResultsContent}
				</TableContainer>
				{limit !== undefined && (
					<TablePagination
						aria-label="table pagination"
						role="toolbar"
						rowsPerPageOptions={TABLE_SETTING.ROWS_PER_PAGE_OPTIONS}
						component="div"
						count={total}
						rowsPerPage={limit}
						page={page <= total / limit ? page : 0}
						onPageChange={(_, value) => onPageChange({ page: value, limit })}
						onRowsPerPageChange={(_, { props: { value } }) =>
							onPageChange({ limit: value, page: 0 })
						}
						labelRowsPerPage={t('rows_per_page')}
						labelDisplayedRows={({ from, to, count }) =>
							`${from}–${to} ${t('of')} ${
								count !== -1 ? count : `more than ${to}`
							}`
						}
					/>
				)}
			</Paper>
		</Box>
	);
}

Table.propTypes = {
	items: PropTypes.array,
	total: PropTypes.number,
	onPageChange: PropTypes.func,
	pagination: PropTypes.shape({
		limit: PropTypes.number, // rows per page
		page: PropTypes.number, // current page
	}),
	sorts: SortsPropType,
	onSort: PropTypes.func,
	children: PropTypes.node,
	columnConfig: columnConfigPropType,
	columnOrder: PropTypes.array, // array of keys
	columnVisibility: PropTypes.object, // key/bool pairs
	isLoading: PropTypes.bool,
	renderColumnHeader: PropTypes.func,
	noResultsContent: PropTypes.any,
	TableRowProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
	TableRowComponent: PropTypes.object,
	TableBodyComponent: PropTypes.object,
	TableHeadComponent: PropTypes.object,
	TableCellComponent: PropTypes.object,
	defaultCellValue: PropTypes.any,
};

export default Table;
